import React from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage, type SupportedLanguage } from "../../contexts/LanguageContext";
import { translateUrl, SUPPORTED_LANGUAGES, LANGUAGE_LABELS } from "../../utils/url-slug-mapping";
import { isGermanOnlyPath } from "../../utils/german-only-paths";

type Language = {
    code: SupportedLanguage;
    name: string;
};

// Derived from the central language list — single source of truth in url-slug-mapping.
const languages: Language[] = SUPPORTED_LANGUAGES.map(code => ({ code, name: LANGUAGE_LABELS[code] }));

export type LanguageSwitcherProps = {
    variant?: "light" | "dark" | "transparent-dark";
    listMode?: boolean; // When true, shows languages as an expanded list instead of a popover
};

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ variant = "light", listMode = false }) => {
    const { language: currentLanguage, content } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const [isOpen, setIsOpen] = React.useState(false);
    const [buttonRect, setButtonRect] = React.useState<DOMRect | null>(null);
    const buttonRef = React.useRef<HTMLButtonElement>(null);

    const current = languages.find(lang => lang.code === currentLanguage) || languages[0];

    const updateButtonPosition = () => {
        if (buttonRef.current) {
            setButtonRect(buttonRef.current.getBoundingClientRect());
        }
    };

    const handleToggle = () => {
        if (!isOpen) {
            updateButtonPosition();
        }
        setIsOpen(!isOpen);
    };

    const handleLanguageChange = (languageCode: SupportedLanguage) => {
        // Translate the current URL to the target language and navigate there.
        // The language state follows the URL via LanguageContext's effect — we
        // deliberately do NOT call setLanguage here, otherwise a render with the
        // new language but the still-old path would race the navigation and
        // require a second click.
        //
        // A German-only page (`german-only-paths.ts`) has no twin: translating
        // its path would land on a 404, so the switcher goes to the EN homepage.
        const translatedPath =
            languageCode !== "de" && isGermanOnlyPath(location.pathname)
                ? `/${languageCode}/`
                : translateUrl(location.pathname, currentLanguage, languageCode);
        navigate(translatedPath);
        setIsOpen(false);
    };

    // Update button position on scroll and resize
    React.useEffect(() => {
        if (isOpen) {
            const handleUpdate = () => updateButtonPosition();
            window.addEventListener("scroll", handleUpdate, true);
            window.addEventListener("resize", handleUpdate);
            return () => {
                window.removeEventListener("scroll", handleUpdate, true);
                window.removeEventListener("resize", handleUpdate);
            };
        }
    }, [isOpen]);

    // Close on ESC key
    React.useEffect(() => {
        if (isOpen) {
            const handleEsc = (e: KeyboardEvent) => {
                if (e.key === "Escape") {
                    setIsOpen(false);
                }
            };
            document.addEventListener("keydown", handleEsc);
            return () => document.removeEventListener("keydown", handleEsc);
        }
    }, [isOpen]);

    const isDark = variant === "dark";
    const isTransparentDark = variant === "transparent-dark";
    const buttonColorClasses = isDark
        ? "bg-white/10 backdrop-blur text-white hover:bg-white/20"
        : isTransparentDark
          ? "text-white hover:bg-neutral-100/20"
          : "bg-white/60 backdrop-blur text-gray-800 hover:bg-white/80";
    const buttonClasses = `flex items-center gap-2 px-3 py-2 rounded-lg  transition-all duration-300 cursor-pointer ${buttonColorClasses}`;

    // List mode: show languages as an expanded list (for mobile navigation)
    if (listMode) {
        return (
            <div className="w-full">
                <button
                    ref={buttonRef}
                    onClick={handleToggle}
                    className={`w-full flex items-center justify-between py-4 px-2 text-white text-lg font-medium min-h-[56px] active:bg-white/10 transition-colors ${
                        isOpen ? "" : "border-b border-white/10"
                    }`}
                    aria-label={
                        (content?.common as { accessibility?: { changeLanguage?: string } })?.accessibility
                            ?.changeLanguage || "Change language"
                    }
                    aria-expanded={isOpen}
                >
                    <span>{current.name}</span>
                    <svg
                        className={`w-5 h-5 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                    }`}
                >
                    <div className="space-y-0">
                        {languages.map(language => (
                            <button
                                key={language.code}
                                onClick={() => handleLanguageChange(language.code)}
                                className={`w-full flex items-center justify-between py-4 px-2 text-white text-lg font-medium min-h-[56px] active:bg-white/10 transition-colors border-b border-white/10 ${
                                    language.code === currentLanguage ? "bg-white/5" : ""
                                }`}
                            >
                                <span>{language.name}</span>
                                {language.code === currentLanguage && <span className="text-white">✓</span>}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // Popover mode: show languages in a popover (default for desktop)
    return (
        <>
            <div className="relative">
                <button
                    ref={buttonRef}
                    onClick={handleToggle}
                    className={buttonClasses}
                    aria-label={
                        (content?.common as { accessibility?: { changeLanguage?: string } })?.accessibility
                            ?.changeLanguage || "Change language"
                    }
                    aria-expanded={isOpen}
                >
                    <span className="text-sm font-medium">{current.name}</span>
                    <ChevronDown
                        className={`w-4 h-4 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                    />
                </button>

                {/* Dropdown - using portal for proper positioning */}
                {isOpen && buttonRect && (
                    <>
                        {createPortal(
                            <div
                                className="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 backdrop-blur z-[1000]"
                                style={{
                                    top: buttonRect.bottom + 8,
                                    right: document.documentElement.clientWidth - buttonRect.right,
                                }}
                            >
                                <div className="py-2">
                                    {languages.map(language => (
                                        <button
                                            key={language.code}
                                            onClick={() => handleLanguageChange(language.code)}
                                            className={`w-full flex items-center gap-3 px-4 py-2 text-left
                                                hover:bg-gray-50 transition-colors duration-200 cursor-pointer ${
                                                    language.code === currentLanguage
                                                        ? "bg-primary-dark/10 text-primary"
                                                        : "text-gray-700"
                                                }`}
                                        >
                                            <span className="text-sm font-medium">{language.name}</span>
                                            {language.code === currentLanguage && (
                                                <span className="ml-auto text-primary">✓</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>,
                            document.body
                        )}

                        {/* Overlay to close dropdown when clicking outside */}
                        {createPortal(
                            <div className="fixed inset-0 z-[800]" onClick={() => setIsOpen(false)} />,
                            document.body
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default LanguageSwitcher;
